import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, Checkbox, Chip, DialogActions, DialogContent, DialogTitle, Grid, Icon, ListItemText, MenuItem, TextField, Typography } from "@mui/material";
import CustomDialog from 'components/custom/CustomDialog';
import { CustomButton } from 'components/roundedbutton/CustomButton';
import useCatchApiError from "hooks/useCatchApiError";
import { IAbstractModel } from 'model/abstract.model';
import { FlowVendedorDTO } from "pages/registra-venda/OutrosComissionados";
import RegistraVendaService from 'pages/registra-venda/registra-venda.service';
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { currencyOf, includesStringInOrder } from 'utils/util';
import VendaService from "../venda.service";
import { ItemRelatorioVendas } from "../vendas.model";
import { theme } from 'theme';


type EditaVendedorProps = {
    itemRef: React.MutableRefObject<ItemRelatorioVendas | null>;
    onClose: () => void;
    onEditaVendedor: (vendedor: IAbstractModel | null, outrosComissionados: Array<string>) => void;
};

type EditaVendedorInitialState = {
    vendedor: FlowVendedorDTO | null,
    outrosComissionadosSelected: Array<FlowVendedorDTO>,
    guias: Array<string>;
};

const initialState: EditaVendedorInitialState = {
    vendedor: null,
    outrosComissionadosSelected: [],
    guias: []
};

const EditaVendedor = (props: EditaVendedorProps) => {
    const service = RegistraVendaService();
    const [stateLocal, setStateLocal] = useState(initialState);
    const { throwErrorMessage } = useCatchApiError();
    const navigate = useNavigate();
    const vendedorRef = useRef<HTMLInputElement>(null);
    const allVendedores = useRef<FlowVendedorDTO[]>([]);
    const vendaService = VendaService();
    const isFirstRender = useRef(true);

    useEffect(() => {
        getVendedoresAc();
        listarGuiasPorConsumidor(props.itemRef.current?.clienteUUID);
        //eslint-disable-next-line
    }, []);

    const getVendedoresAc = (toSearch: string = '') => {
        service.getVendedoresAc(toSearch)
            .then(({ data }) => {
                allVendedores.current = data;
                setVendedoresFromPedido();
            }).catch(error => {
                throwErrorMessage(error);
                navigate('/caixa');
            });
    };

    const setVendedoresFromPedido = () => {
        if (!isFirstRender.current) return;
        service.getVendedoresDoPedido(props.itemRef.current!.uuidPedido)
            .then(({ data }) => {
                setStateLocal(prev => {
                    return {
                        ...prev,
                        vendedor: allVendedores.current.find(s => data.vendedorPrincipal === s.uuid) ?? null,
                        outrosComissionadosSelected: allVendedores.current.filter(s => data.outrosComissionados.includes(s.uuid)),
                    };
                });
                isFirstRender.current = false;
            })
            .catch(throwErrorMessage);
    };

    const listarGuiasPorConsumidor = async (uuid: string | null | undefined) => {
        if (!uuid) return;
        try {
            const { data } = await vendaService.listarGuiasPorConsumidor(uuid);
            setStateLocal(prevState => ({
                ...prevState,
                guias: data
            }));
        } catch {

        }
    };

    const onSelectVendedor = useCallback((e: SyntheticEvent<Element, Event>, value: IAbstractModel | null) => {
        setStateLocal(prevState => ({ ...prevState, vendedor: value }));
    }, []);


    const renderOptions = (option: FlowVendedorDTO) => {
        const isGuia = stateLocal.guias.some(e => e === option.uuid);
        return (
            <MenuItem
                onClick={() =>
                    setStateLocal(prev => {
                        const outrosComissionadosSelected = !!prev.outrosComissionadosSelected.find(s => s.uuid === option.uuid)
                            ? prev.outrosComissionadosSelected.filter(s => s.uuid !== option.uuid)
                            : [...prev.outrosComissionadosSelected, option];
                        return { ...prev, outrosComissionadosSelected };
                    })
                }
                key={option.uuid}
                value={option.descritivo}>
                <Checkbox
                    id={`item-${option.descritivo?.toLowerCase()}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: !isGuia ? theme.palette.primary.main : theme.palette.info.main
                        }
                    }}
                    checked={stateLocal.outrosComissionadosSelected.some(e => e.uuid === option.uuid)}
                />
                <ListItemText primary={`${option.descritivo}${isGuia ? ' (GUIA)' : ''}`} />
            </MenuItem>
        );
    };

    const onRemoveChip = useCallback((uuid: string) => {
        setStateLocal(prevState => ({ ...prevState, outrosComissionadosSelected: prevState.outrosComissionadosSelected.filter(e => e.uuid !== uuid) }));
        //eslint-disable-next-line
    }, []);

    const getNoOptionsText = () => {
        if (!stateLocal.vendedor) {
            return "Selecione um vendedor para carregar os outros comissionados";
        }
        return "Nenhum valor encontrado";
    };

    return (
        <CustomDialog
            open
            onClose={props.onClose}
            fullWidth
            maxWidth={'md'}

        >
            <Grid xs={12} sm={6} lg={6} style={{
                minHeight: '150px'
            }}>
                <DialogTitle id="dialog-title" textAlign={'center'}>
                    <Typography fontSize={30} paddingTop={0}>Selecione um vendedor</Typography>
                </DialogTitle>

                <DialogContent id="dialog-content" sx={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <Typography>Pedido:</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography>{props.itemRef.current?.numeroPedido}</Typography>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Typography>Cliente:</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography>{props.itemRef.current?.cliente}</Typography>

                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Typography>Total do Pedido:</Typography>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Typography>{currencyOf(props.itemRef.current?.valorPedido)}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>

                <Autocomplete
                    onBlur={() => getVendedoresAc("")}
                    id="acVendedor"
                    value={stateLocal.vendedor}
                    options={allVendedores.current}
                    getOptionLabel={(option) => option.descritivo}
                    onChange={onSelectVendedor}
                    filterOptions={(filter, state) => filter.filter(
                        s => !stateLocal.guias.includes(s.uuid)
                            && !stateLocal.outrosComissionadosSelected.find(o => o.uuid === s.uuid)
                            && includesStringInOrder(s.descritivo.replace('.', '').replace('-', ''), state.inputValue)
                    )}
                    noOptionsText={'Nenhum resultado encontrado'}
                    renderInput={
                        (params) => <TextField {...params}
                            label="Vendedor"
                            variant="standard"
                            inputRef={vendedorRef}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <h4 className="h4-formatado">(F6)</h4>
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />}
                />

                <Autocomplete
                    sx={{ overflowX: 'hidden', overflowY: 'auto', width: '100%', marginTop: '10px' }}
                    multiple
                    value={stateLocal.outrosComissionadosSelected}
                    options={allVendedores.current}
                    filterOptions={(filter, state) => filter.filter(s => stateLocal.vendedor?.uuid !== s.uuid
                        && includesStringInOrder(s.descritivo.replace('.', '').replace('-', ''), state.inputValue))}
                    getOptionLabel={(option) => option.descritivo}
                    onChange={(_, value) => setStateLocal(prev => ({ ...prev, outrosComissionadosSelected: value }))}
                    noOptionsText={getNoOptionsText()}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="standard"
                            label="Outros Comissionados"
                        />)}
                    disableClearable
                    renderOption={(_, option) => renderOptions(option)}
                    renderTags={(p) => p.map((e, i) => {
                        const isGuia = stateLocal.guias.some(el => el === e.uuid);
                        return (
                            <Chip
                                key={e.uuid}
                                sx={{
                                    color: theme.palette.background.default,
                                    background: isGuia ? theme.palette.info.main : theme.palette.primary.main,
                                    marginRight: '3px',
                                }}
                                onDelete={() => onRemoveChip(e.uuid)}
                                label={e.descritivo.split(" - ")[1] ?? ''}
                                deleteIcon={<CloseIcon sx={{ color: `${theme.palette.background.default} !important` }} />}
                                size='small'
                            />
                        );
                    })}
                />
            </Grid>


            <DialogActions
                sx={{
                    justifyContent: 'space-between',
                    marginTop: '40px'
                }}>
                <CustomButton onClick={props.onClose} variant="contained" color="error" startIcon={<Icon className='fa-solid fa-xmark' />}>
                    Fechar
                </CustomButton>
                <CustomButton
                    type="submit"
                    onClick={() => props.onEditaVendedor(stateLocal.vendedor, stateLocal.outrosComissionadosSelected.map(e => e.uuid))}
                    disabled={!stateLocal.vendedor}
                    variant="contained"
                    color="success"
                    startIcon={<Icon className='fa-solid fa-check' />}
                >
                    Confirmar
                </CustomButton>
            </DialogActions>
        </CustomDialog >
    );
};

export default EditaVendedor;
